/**
 * SQLite Storage Adapter
 * Uses Capacitor SQLite for native apps and sql.js for web
 */

import { Capacitor } from '@capacitor/core';

interface StorageAdapter {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  clear(): Promise<void>;
}

// For web, we'll use a simple key-value table structure
// For native, we'll use @capacitor-community/sqlite (requires installation)

class SQLiteAdapter implements StorageAdapter {
  private dbName = 'fishapp_storage';
  private tableName = 'key_value_store';
  private initialized = false;
  private db: any = null;
  private initPromise: Promise<void> | null = null;

  constructor() {
    // Start initialization but don't block constructor
    this.initPromise = this.initialize();
  }

  private async initialize(): Promise<void> {
    if (this.initialized) return;
    
    // Prevent concurrent initialization attempts
    if (this.initPromise && this.initPromise !== Promise.resolve()) {
      await this.initPromise;
      return;
    }

    try {
      if (Capacitor.isNativePlatform()) {
        // Native platform: Use @capacitor-community/sqlite
        try {
          // @ts-ignore
          const { CapacitorSQLite, SQLiteConnection } = await import('@capacitor-community/sqlite');
          
          const sqlite = new SQLiteConnection(CapacitorSQLite);
          
          // Create connection
          await sqlite.createConnection(
            this.dbName,
            false, // encrypted
            'no-encryption', // mode
            1, // version
            false // readonly
          );

          // Open database
          this.db = await sqlite.retrieveConnection(this.dbName, false);
          await this.db.open();

          // Create table if not exists
          await this.db.execute(
            `CREATE TABLE IF NOT EXISTS ${this.tableName} (
              key TEXT PRIMARY KEY NOT NULL,
              value TEXT NOT NULL
            );`,
            false
          );

          this.initialized = true;
          console.log('✅ SQLite initialized successfully on native platform');
        } catch (importError: any) {
          console.error('❌ @capacitor-community/sqlite plugin error:', importError);
          console.log('Falling back to IndexedDB');
          await this.initializeIndexedDB();
          this.initialized = true;
        }
      } else {
        // Web platform: Use IndexedDB as fallback (simpler than sql.js)
        // This provides similar performance characteristics to SQLite
        await this.initializeIndexedDB();
        this.initialized = true;
      }
    } catch (error) {
      console.error('SQLite initialization error:', error);
      throw new Error('Failed to initialize SQLite storage. Falling back to localStorage.');
    }
  }

  private async initializeIndexedDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.tableName)) {
          db.createObjectStore(this.tableName, { keyPath: 'key' });
        }
      };
    });
  }

  async getItem(key: string): Promise<string | null> {
    await this.initialize();

    if (!this.db) {
      console.error('SQLite adapter not initialized - database unavailable');
      return null;
    }

    try {
      if (Capacitor.isNativePlatform() && this.db.query) {
        // Native SQLite query
        const result = await this.db.query(
          `SELECT value FROM ${this.tableName} WHERE key = ?`,
          [key]
        );

        if (result.values && result.values.length > 0) {
          return result.values[0].value;
        }
        return null;
      } else {
        // IndexedDB query
        return new Promise((resolve, reject) => {
          const transaction = this.db.transaction([this.tableName], 'readonly');
          const store = transaction.objectStore(this.tableName);
          const request = store.get(key);

          request.onsuccess = () => {
            resolve(request.result?.value || null);
          };

          request.onerror = () => {
            reject(new Error('Failed to get item from IndexedDB'));
          };
        });
      }
    } catch (error) {
      console.error('SQLite getItem error:', error);
      return null;
    }
  }

  async setItem(key: string, value: string): Promise<void> {
    await this.initialize();

    if (!this.db) {
      throw new Error('SQLite adapter not initialized - database unavailable');
    }

    try {
      if (Capacitor.isNativePlatform() && this.db.run) {
        // Native SQLite insert/update
        await this.db.run(
          `INSERT INTO ${this.tableName} (key, value) VALUES (?, ?)
           ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
          [key, value]
        );
      } else {
        // IndexedDB put
        await new Promise<void>((resolve, reject) => {
          const transaction = this.db.transaction([this.tableName], 'readwrite');
          const store = transaction.objectStore(this.tableName);
          const request = store.put({ key, value });

          request.onsuccess = () => resolve();
          request.onerror = () => reject(new Error('Failed to set item in IndexedDB'));
        });
      }
    } catch (error) {
      console.error('SQLite setItem error:', error);
      throw error;
    }
  }

  async removeItem(key: string): Promise<void> {
    await this.initialize();

    if (!this.db) {
      throw new Error('SQLite adapter not initialized - database unavailable');
    }

    try {
      if (Capacitor.isNativePlatform() && this.db.run) {
        // Native SQLite delete
        await this.db.run(
          `DELETE FROM ${this.tableName} WHERE key = ?`,
          [key]
        );
      } else {
        // IndexedDB delete
        await new Promise<void>((resolve, reject) => {
          const transaction = this.db.transaction([this.tableName], 'readwrite');
          const store = transaction.objectStore(this.tableName);
          const request = store.delete(key);

          request.onsuccess = () => resolve();
          request.onerror = () => reject(new Error('Failed to remove item from IndexedDB'));
        });
      }
    } catch (error) {
      console.error('SQLite removeItem error:', error);
      throw error;
    }
  }

  async clear(): Promise<void> {
    await this.initialize();

    if (!this.db) {
      throw new Error('SQLite adapter not initialized - database unavailable');
    }

    try {
      if (Capacitor.isNativePlatform() && this.db.execute) {
        // Native SQLite clear
        await this.db.execute(
          `DELETE FROM ${this.tableName}`,
          false
        );
      } else {
        // IndexedDB clear
        await new Promise<void>((resolve, reject) => {
          const transaction = this.db.transaction([this.tableName], 'readwrite');
          const store = transaction.objectStore(this.tableName);
          const request = store.clear();

          request.onsuccess = () => resolve();
          request.onerror = () => reject(new Error('Failed to clear IndexedDB'));
        });
      }
    } catch (error) {
      console.error('SQLite clear error:', error);
      throw error;
    }
  }

  // Helper method to get all keys (useful for migration)
  async getAllKeys(): Promise<string[]> {
    await this.initialize();

    try {
      if (Capacitor.isNativePlatform() && this.db && this.db.query) {
        const result = await this.db.query(
          `SELECT key FROM ${this.tableName}`,
          []
        );
        return result.values ? result.values.map((row: any) => row.key) : [];
      } else if (this.db) {
        return new Promise((resolve, reject) => {
          const transaction = this.db.transaction([this.tableName], 'readonly');
          const store = transaction.objectStore(this.tableName);
          const request = store.getAllKeys();

          request.onsuccess = () => resolve(request.result as string[]);
          request.onerror = () => reject(new Error('Failed to get all keys from IndexedDB'));
        });
      }
      return [];
    } catch (error) {
      console.error('SQLite getAllKeys error:', error);
      return [];
    }
  }
}

export default SQLiteAdapter;
